import { useCallback, useSyncExternalStore } from 'react';

/**
 * Subscribe to a CSS media query from JavaScript.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: the first paint
 * then reads the real value instead of rendering a wrong one and correcting it,
 * which is what produced a visible flash of the desktop layout on a phone.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const media = window.matchMedia(query);
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    // Server/prerender has no viewport; assume the wide layout, which is what
    // the markup is written for.
    () => false,
  );
}

/**
 * Below Tailwind's `lg`, where the sidebar becomes a drawer and the task detail
 * takes the whole screen. Kept in one place so the CSS breakpoint and the
 * behaviour that depends on it cannot drift apart.
 */
export function useIsCompact(): boolean {
  return useMediaQuery('(max-width: 1023.98px)');
}
